import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { UserRole } from "@/components/UserRole";
import { UserFeatures } from "@/components/UserFeatures";
import { User, Shield, Layers } from "lucide-react";
import { useContentManager } from "@/hooks/useContentManager";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { preloadView } = useContentManager();
  const { user } = useAuth();

  const handleProfileClick = () => {
    navigate("/dashboard/profile");
  };

  // Preload the users page when hovering over the 'View All Users' button
  const handleUsersHover = () => {
    preloadView("/dashboard/users");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-120px)]">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* User Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="col-span-1"
        >
          <Card className="h-full card-hover">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-lg">User Profile</CardTitle>
                <CardDescription>Your account information</CardDescription>
              </div>
              <User className="h-6 w-6 text-slate-400" />
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                <p className="text-slate-700">
                  <span className="font-medium">Email:</span> {user?.email}
                </p>
                <p className="text-slate-700">
                  <span className="font-medium">Last login:</span>{" "}
                  {user?.last_sign_in_at
                    ? formatDistanceToNow(new Date(user.last_sign_in_at), { addSuffix: true })
                    : "N/A"}
                </p>
              </div>
            </CardContent>
            <CardFooter className="border-t pt-4">
              <Button variant="outline" size="sm" className="w-full" onClick={handleProfileClick}>
                Edit Profile
              </Button>
            </CardFooter>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="col-span-1"
        >
          <Card className="h-full card-hover">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-lg">Access Level</CardTitle>
                <CardDescription>Your role and permissions</CardDescription>
              </div>
              <Shield className="h-6 w-6 text-slate-400" />
            </CardHeader>
            <CardContent className="pt-4">
              {user && <UserRole userId={user.id} />}
            </CardContent>
            <CardFooter className="border-t pt-4">
              <p className="text-sm text-slate-500">
                Contact administration for role changes
              </p>
            </CardFooter>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="col-span-1"
        >
          <Card className="h-full card-hover">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-lg">Welcome</CardTitle>
                <CardDescription>Getting started guide</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-slate-600">
                Welcome to your dashboard! This is where you can manage your account and access your content.
              </p>
            </CardContent>
            <CardFooter className="border-t pt-4">
              <Button variant="link" size="sm" className="p-0">
                Learn more about the platform
              </Button>
            </CardFooter>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="col-span-1 md:col-span-2 lg:col-span-3"
        >
          <Card className="card-hover">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-lg">Available Features</CardTitle>
                <CardDescription>Features available with your current plan</CardDescription>
              </div>
              <Layers className="h-6 w-6 text-slate-400" />
            </CardHeader>
            <CardContent className="pt-4">
              {user && <UserFeatures userId={user.id} />}
            </CardContent>
            <CardFooter className="border-t pt-4">
              <Button 
                variant="outline" 
                size="sm"
                onMouseEnter={handleUsersHover}
                onClick={() => navigate("/dashboard/users")}
              >
                View All Users
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Dashboard;
